import type {
  ChartConfiguration,
  ChartOptions,
  ChartDataset,
} from 'chart.js/auto';
import { Chart as ChartJS } from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import datalabelsPlugin from 'chartjs-plugin-datalabels';
import zoomPlugin from 'chartjs-plugin-zoom';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type {
  DatasetInterface,
  FlowCurvePointInterface,
} from 'containers/FlowProperties/components/FlowCurve';
import { getDatasetMinMaxX } from 'containers/FlowProperties/util';
import theme from 'theme/theme';
import { getLineOptions } from 'util/getLineChartOptions';
import type { AnnotationOptions } from 'chartjs-plugin-annotation';

ChartJS.register(annotationPlugin);
ChartJS.register(datalabelsPlugin);
ChartJS.register(zoomPlugin);
ChartJS.defaults.font.family = 'Sandvik Sans Text';
ChartJS.defaults.font.size = 14;
ChartJS.defaults.plugins.legend.display = false;
ChartJS.defaults.set('plugins.datalabels', {
  display: false,
});

interface CreateFlowCurveInterface {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  datasets: DatasetInterface[];
  pxxPoints: FlowCurvePointInterface[] | undefined;
  lowerLimit: number | undefined;
  upperLimit: number | undefined;
  showGridLines: boolean;
  psdCalcMode: string;
  metricType: { value: string };
  setLimits: (lower: number, upper: number) => void;
  activeDatasetIndex?: number;
  ticks?: number[];
  enableWheelZoom?: boolean;
  enablePan?: boolean;
}

const insertPxxPoints = (
  data: FlowCurvePointInterface[],
  pxxPoints: FlowCurvePointInterface[] | undefined,
  interpolatedIndexes: number[],
): FlowCurvePointInterface[] => {
  if (!pxxPoints) return data;

  const newData = [...data];
  pxxPoints.forEach((point) => {
    const insertIndex = newData.findIndex((d) => d.y > point.y);
    if (insertIndex !== -1) {
      newData.splice(insertIndex, 0, point);
      interpolatedIndexes.push(insertIndex);
    }
  });
  return newData;
};

const createAnnotationsForPoints = (
  points: FlowCurvePointInterface[],
): AnnotationOptions[] => {
  return points.flatMap((point) => [
    {
      type: 'line' as const,
      borderDash: [5, 5],
      borderWidth: 1,
      mode: 'horizontal' as const,
      scaleID: 'y',
      value: point.y,
      borderColor: theme.graph.colors.primaryLine,
    },
    {
      type: 'line' as const,
      borderDash: [5, 5],
      borderWidth: 1,
      mode: 'vertical' as const,
      scaleID: 'x',
      value: point.x,
      borderColor: theme.graph.colors.primaryLine,
    },
  ]);
};

export const LineChart = ({
  canvasRef,
  datasets,
  pxxPoints,
  lowerLimit,
  upperLimit,
  showGridLines,
  psdCalcMode,
  metricType,
  activeDatasetIndex,
  setLimits,
  ticks,
  enableWheelZoom,
  enablePan,
}: CreateFlowCurveInterface) => {
  const chartInstanceRef = useRef<ChartJS<'line'> | null>(null);

  useEffect(() => {
    const ctx = canvasRef.current;
    if (!ctx) return;

    const context = ctx.getContext('2d', { willReadFrequently: true });
    if (!context) return;

    const interpolatedIndexes: number[] = [];
    const annotations: AnnotationOptions[] = [];

    const datasetsWithPxx = datasets
      .map((ds, index) => {
        if (index === activeDatasetIndex && ds.data && pxxPoints) {
          const newData = insertPxxPoints(
            ds.data,
            pxxPoints,
            interpolatedIndexes,
          );
          annotations.push(...createAnnotationsForPoints(pxxPoints));

          return {
            ...ds,
            cubicInterpolationMode: 'default' as const,
            tension: 0.1,
            data: newData,
          };
        }

        return {
          ...ds,
          cubicInterpolationMode: 'default' as const,
          tension: 0.1,
        };
      })
      .filter((ds) => ds.data !== undefined);

    const { smallestX, biggestX } = getDatasetMinMaxX(datasetsWithPxx);

    const lineOptions: ChartOptions<'line'> = getLineOptions({
      lowerLimit,
      upperLimit,
      smallestX,
      biggestX,
      annotations,
      showGridLines,
      psdCalcMode,
      metricType,
      setLimits,
      ticks,
      enableWheelZoom,
      enablePan,
    });

    const chartConfig: ChartConfiguration<'line'> = {
      type: 'line',
      data: {
        datasets: datasetsWithPxx as unknown as ChartDataset<'line'>[],
      },
      options: lineOptions,
    };

    if (chartInstanceRef.current) {
      chartInstanceRef.current.data.datasets =
        datasetsWithPxx as unknown as ChartDataset<'line'>[];
      chartInstanceRef.current.options = lineOptions;
      chartInstanceRef.current.update('none');
    } else {
      chartInstanceRef.current = new ChartJS(ctx, chartConfig);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [
    canvasRef,
    datasets,
    pxxPoints,
    lowerLimit,
    upperLimit,
    showGridLines,
    psdCalcMode,
    metricType,
    activeDatasetIndex,
    setLimits,
    ticks,
    enableWheelZoom,
    enablePan,
  ]);

  return null;
};
